// Tiny CSV parser + sales-row extractor.
//
// Why our own parser instead of papaparse: keeps the bundle small and
// avoids a dependency for a single-purpose feature. Handles the cases
// Toast Sales Summary exports actually produce: comma-separated, optional
// double-quoted fields, escaped quotes inside quoted fields ("" → "),
// CRLF or LF line endings, trailing newline. Doesn't try to handle every
// edge case in RFC 4180 — if a future POS export breaks this, we swap in
// papaparse then.
//
// The exported parseSalesCsv() does the full job: parses → detects
// columns → extracts {sale_date, gross_sales, net_sales, transaction_count}
// rows. Auto-detects column names so it works across POS vendors that
// use slightly different header text ("Net Sales" vs "Total Net Sales"
// vs "Net Total" etc.).

// ---------- Low-level CSV → rows -----------------------------
export function parseCsv(text) {
  const rows = []
  let cur = []
  let field = ''
  let i = 0
  let inQuotes = false
  const len = text.length

  while (i < len) {
    const ch = text[i]

    if (inQuotes) {
      if (ch === '"') {
        // Escaped quote ("") → literal "
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        inQuotes = false
        i += 1
        continue
      }
      field += ch
      i += 1
      continue
    }

    if (ch === '"') {
      inQuotes = true
      i += 1
      continue
    }
    if (ch === ',') {
      cur.push(field)
      field = ''
      i += 1
      continue
    }
    if (ch === '\r') {
      // Skip — handled with \n
      i += 1
      continue
    }
    if (ch === '\n') {
      cur.push(field)
      // Skip empty lines
      if (cur.length > 1 || cur[0] !== '') rows.push(cur)
      cur = []
      field = ''
      i += 1
      continue
    }
    field += ch
    i += 1
  }
  // Last row (no trailing newline)
  if (field !== '' || cur.length > 0) {
    cur.push(field)
    if (cur.length > 1 || cur[0] !== '') rows.push(cur)
  }
  return rows
}

// ---------- Column detection --------------------------------
// Score each header against a target so we pick the best column,
// not the first plausible match. Higher score = better match.
function scoreHeader(header, targetWords, antiWords = []) {
  const h = header.toLowerCase()
  let score = 0
  for (const w of targetWords) if (h.includes(w)) score += 1
  for (const w of antiWords) if (h.includes(w)) score -= 2
  return score
}

function detectColumns(headers) {
  const findBest = (target, anti = []) => {
    let bestIdx = -1
    let bestScore = 0
    headers.forEach((h, idx) => {
      const s = scoreHeader(h, target, anti)
      if (s > bestScore) { bestScore = s; bestIdx = idx }
    })
    return bestIdx
  }

  return {
    // Date column — "business date", "date", "day", or format-name
    // headers Toast actually exports like "yyyyMMdd" / "yyyy-mm-dd".
    dateIdx: findBest(['date', 'day', 'business', 'yyyy', 'yymm', 'mmdd']),
    // Net sales — prefer columns containing both "net" and "sales".
    // Anti-match "tax" so we don't pick "Net Tax" or similar.
    netIdx: findBest(['net', 'sales'], ['tax', 'tip', 'discount', 'refund']),
    // Gross sales — same idea
    grossIdx: findBest(['gross', 'sales'], ['tax', 'tip', 'discount', 'refund']),
    // Transaction count — "checks", "orders", "transactions", "count",
    // "guests" (Toast's "Total guests" column is essentially cover count)
    txIdx: findBest(['check', 'order', 'transaction', 'count', 'guest']),
  }
}

// ---------- Date / number helpers ---------------------------
// Parse various date formats Toast and others emit: 2026-04-28,
// 04/28/2026, 4/28/26, "Apr 28, 2026", and Toast's compact 20260428.
// Returns YYYY-MM-DD or null.
function parseDate(raw) {
  if (!raw) return null
  const s = String(raw).trim().replace(/^"|"$/g, '')
  if (!s) return null

  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)

  // Toast's "yyyyMMdd" format — 8 consecutive digits, no separators.
  // E.g. "20260430" → "2026-04-30". Validate the month/day are sane
  // before accepting so we don't mis-match a long order number.
  const compact = s.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (compact) {
    const [, y, m, d] = compact
    const mm = Number(m), dd = Number(d)
    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return `${y}-${m}-${d}`
    }
  }

  // M/D/YYYY or M/D/YY
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/)
  if (slash) {
    const [, m, d, yRaw] = slash
    const yyyy = yRaw.length === 2 ? `20${yRaw}` : yRaw
    return `${yyyy}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  // Fall back to Date() — handles "Apr 28, 2026" and similar
  const t = Date.parse(s)
  if (!isNaN(t)) {
    const d = new Date(t)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  return null
}

// Strip "$", commas, parentheses (negatives), surrounding whitespace.
function parseMoney(raw) {
  if (raw == null) return null
  const s = String(raw).trim().replace(/^"|"$/g, '')
  if (!s) return null
  const negative = /^\(.*\)$/.test(s)
  const cleaned = s.replace(/[,$\s()]/g, '')
  const n = Number(cleaned)
  if (!isFinite(n)) return null
  return negative ? -n : n
}

function parseInt10(raw) {
  if (raw == null) return null
  const s = String(raw).trim().replace(/[",\s]/g, '')
  if (!s) return null
  const n = parseInt(s, 10)
  return isFinite(n) ? n : null
}

// ---------- Top-level: CSV text → import-ready rows ---------
// Returns { rows, columns, headers, errors }.
//   rows    — [{ sale_date, gross_sales, net_sales, transaction_count }]
//   columns — which header indexes were detected for each field
//   headers — the raw header row (for UI display when detection fails)
//   errors  — array of human-readable warnings (skipped rows etc.)
export function parseSalesCsv(text) {
  const errors = []
  const grid = parseCsv(text)
  if (grid.length < 2) {
    return {
      rows: [],
      columns: null,
      headers: grid[0] ?? [],
      errors: ['CSV looks empty — only a header row was found.'],
    }
  }

  const headers = grid[0].map((h) => h.trim())
  const cols = detectColumns(headers)

  if (cols.dateIdx === -1) {
    return {
      rows: [],
      columns: cols,
      headers,
      errors: [
        'Could not find a date column. Expected a header containing "date", "day", or "business".',
      ],
    }
  }
  if (cols.netIdx === -1 && cols.grossIdx === -1) {
    return {
      rows: [],
      columns: cols,
      headers,
      errors: [
        'Could not find a sales column. Expected a header containing "net sales" or "gross sales".',
      ],
    }
  }

  const rows = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    if (cells.length === 0) continue
    const sale_date = parseDate(cells[cols.dateIdx])
    if (!sale_date) {
      errors.push(`Row ${r + 1}: skipped — couldn't parse date "${cells[cols.dateIdx] ?? ''}"`)
      continue
    }
    const net = cols.netIdx >= 0 ? parseMoney(cells[cols.netIdx]) : null
    const gross = cols.grossIdx >= 0 ? parseMoney(cells[cols.grossIdx]) : null
    const tx = cols.txIdx >= 0 ? parseInt10(cells[cols.txIdx]) : null

    if (net == null && gross == null) {
      errors.push(`Row ${r + 1}: skipped — both net and gross sales were empty`)
      continue
    }

    rows.push({
      sale_date,
      net_sales: net,
      gross_sales: gross,
      transaction_count: tx,
    })
  }

  // Deduplicate by sale_date — keep the LAST occurrence so a repeated
  // upload of the same day overwrites cleanly.
  const dedup = new Map()
  for (const row of rows) dedup.set(row.sale_date, row)
  const finalRows = [...dedup.values()].sort((a, b) =>
    a.sale_date < b.sale_date ? -1 : 1
  )

  return { rows: finalRows, columns: cols, headers, errors }
}

// ============================================================
// Item-level imports — Toast Item Sales / Menu Items CSV exports.
// ============================================================
// Used by ItemsImportModal to bulk-create items in the inventory
// catalog from a POS export. Two Toast exports are common targets:
//
//   1. Item Sales report — per-item performance (name, category,
//      qty sold, net sales). Best for "what's actually selling".
//   2. Menu Items / Menu Config export — full catalog (name,
//      category, price). Best for first-time onboarding.
//
// The parser auto-detects which one (or any other POS export) by
// scoring header columns. We extract: name (required), category,
// sale_price (per-item), quantity_sold (informational, used for
// preview stats but not stored on the item row).

function detectItemColumns(headers) {
  const findBest = (target, anti = []) => {
    let bestIdx = -1
    let bestScore = 0
    headers.forEach((h, idx) => {
      const s = scoreHeader(h, target, anti)
      if (s > bestScore) { bestScore = s; bestIdx = idx }
    })
    return bestIdx
  }

  return {
    // Item name — "item name", "menu item", "item", "name"
    nameIdx: findBest(['item', 'name', 'menu', 'product'], ['category', 'group']),
    // Category — "menu group", "category", "section"
    categoryIdx: findBest(['category', 'group', 'section', 'class']),
    // Sale price — "price", "menu price", "sale price"
    priceIdx: findBest(['price'], ['cost', 'wholesale']),
    // Quantity sold — for Item Sales reports. Used for preview
    // stats (top sellers) but not stored on the item row directly.
    qtyIdx: findBest(['quantity', 'qty', 'sold', 'units'], ['gross', 'net', 'price']),
    // Net sales for the item — used to derive average price when
    // a per-item price column isn't present.
    netSalesIdx: findBest(['net', 'sales'], ['tax', 'tip', 'discount']),
    // Toast GUID — if present, lets us match by canonical ID
    // instead of fuzzy name matching. Optional.
    guidIdx: findBest(['guid', 'toast id', 'pos id', 'item id'], []),
  }
}

export function parseItemsCsv(text) {
  const errors = []
  const grid = parseCsv(text)
  if (grid.length < 2) {
    return {
      rows: [],
      columns: null,
      headers: grid[0] ?? [],
      errors: ['CSV looks empty — only a header row was found.'],
    }
  }

  const headers = grid[0].map((h) => h.trim())
  const cols = detectItemColumns(headers)

  if (cols.nameIdx === -1) {
    return {
      rows: [],
      columns: cols,
      headers,
      errors: [
        'Could not find an item-name column. Expected a header containing "item", "name", "menu", or "product".',
      ],
    }
  }

  const rows = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    if (cells.length === 0) continue
    const name = String(cells[cols.nameIdx] ?? '').trim().replace(/^"|"$/g, '')
    if (!name) {
      errors.push(`Row ${r + 1}: skipped — name column was empty`)
      continue
    }
    const category = cols.categoryIdx >= 0
      ? String(cells[cols.categoryIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null
    let sale_price = cols.priceIdx >= 0 ? parseMoney(cells[cols.priceIdx]) : null
    const qty_sold = cols.qtyIdx >= 0 ? parseInt10(cells[cols.qtyIdx]) : null
    const net_sales = cols.netSalesIdx >= 0 ? parseMoney(cells[cols.netSalesIdx]) : null
    const toast_guid = cols.guidIdx >= 0
      ? String(cells[cols.guidIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null

    // If we have qty_sold and net_sales but no per-item price, derive
    // an average price. Useful for Item Sales reports that only show
    // totals.
    if (sale_price == null && qty_sold && qty_sold > 0 && net_sales != null) {
      sale_price = Number((net_sales / qty_sold).toFixed(2))
    }

    rows.push({
      name,
      category,
      sale_price,
      quantity_sold: qty_sold,
      net_sales,
      toast_guid,
    })
  }

  // Deduplicate by name (case-insensitive). When a Toast Item Sales
  // report breaks out the same item across multiple modifiers, keep
  // the row with the highest qty_sold so velocity stats are meaningful.
  const dedup = new Map()
  for (const row of rows) {
    const key = row.name.toLowerCase().trim()
    const prev = dedup.get(key)
    if (!prev) {
      dedup.set(key, row)
    } else {
      // Prefer row with more data — combine sales counts, prefer non-null fields.
      const merged = {
        ...prev,
        category:    prev.category ?? row.category,
        sale_price:  prev.sale_price ?? row.sale_price,
        toast_guid:  prev.toast_guid ?? row.toast_guid,
        quantity_sold: (prev.quantity_sold || 0) + (row.quantity_sold || 0),
        net_sales:   (prev.net_sales || 0) + (row.net_sales || 0),
      }
      dedup.set(key, merged)
    }
  }
  const finalRows = [...dedup.values()].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return { rows: finalRows, columns: cols, headers, errors }
}

// ============================================================
// Item Sales CSV import — Toast Item Sales export
// ============================================================
// Powers the Theoretical-vs-Actual variance feature. Toast's
// "Item Sales" CSV export gives us per-menu-item quantities + revenue
// for a date range the manager picked at export time. The CSV itself
// usually does NOT contain that date range, so the import modal asks
// for period_start + period_end alongside the file.
//
// Column detection reuses detectItemColumns from the catalog importer
// — same Toast headers (Menu Item, Items Sold, Net Sales, etc.). We
// also look for a gross-sales column here, which the catalog parser
// ignores because it's not relevant for setting items up.
//
// Output shape: { rows, columns, headers, errors } where each row is
//   { item_name, quantity_sold, net_sales, gross_sales, toast_guid }
// (period_start + period_end are added by the caller from the modal's
//  date pickers, not extracted from the CSV.)

export function parseItemSalesCsv(text) {
  const errors = []
  const grid = parseCsv(text)
  if (grid.length < 2) {
    return {
      rows: [],
      columns: null,
      headers: grid[0] ?? [],
      errors: ['CSV looks empty — only a header row was found.'],
    }
  }

  const headers = grid[0].map((h) => h.trim())
  const cols = detectItemColumns(headers)

  // Item Sales reports usually have a Gross Sales column too. Locate
  // it without disrupting detectItemColumns (which the catalog
  // importer also depends on).
  let grossIdx = -1
  let bestScore = 0
  headers.forEach((h, idx) => {
    const s = scoreHeader(h, ['gross', 'sales'], ['tax', 'tip', 'discount', 'refund'])
    if (s > bestScore) { bestScore = s; grossIdx = idx }
  })

  if (cols.nameIdx === -1) {
    return {
      rows: [],
      columns: { ...cols, grossIdx },
      headers,
      errors: [
        'Could not find an item-name column. Expected a header containing "item", "name", "menu", or "product".',
      ],
    }
  }
  if (cols.qtyIdx === -1) {
    return {
      rows: [],
      columns: { ...cols, grossIdx },
      headers,
      errors: [
        'Could not find a quantity-sold column. Expected a header containing "quantity", "items sold", or "units".',
      ],
    }
  }

  const rows = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    if (cells.length === 0) continue
    const item_name = String(cells[cols.nameIdx] ?? '').trim().replace(/^"|"$/g, '')
    if (!item_name) {
      errors.push(`Row ${r + 1}: skipped — name column was empty`)
      continue
    }
    const quantity_sold = parseInt10(cells[cols.qtyIdx])
    if (quantity_sold == null || quantity_sold <= 0) {
      // Toast often emits zero-sold rows for items on the menu but
      // unsold this period — those carry no info for variance, so skip.
      continue
    }
    const net_sales   = cols.netSalesIdx >= 0 ? parseMoney(cells[cols.netSalesIdx]) : null
    const gross_sales = grossIdx        >= 0 ? parseMoney(cells[grossIdx])        : null
    const toast_guid  = cols.guidIdx    >= 0
      ? String(cells[cols.guidIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null

    rows.push({
      item_name,
      quantity_sold,
      net_sales,
      gross_sales,
      toast_guid,
    })
  }

  // Same modifier-row dedup as the catalog import: same item appearing
  // multiple times (e.g. "Brisket Sandwich" + variants) gets summed.
  const dedup = new Map()
  for (const row of rows) {
    const key = row.item_name.toLowerCase().trim()
    const prev = dedup.get(key)
    if (!prev) {
      dedup.set(key, row)
    } else {
      dedup.set(key, {
        ...prev,
        quantity_sold: (prev.quantity_sold || 0) + (row.quantity_sold || 0),
        net_sales:     (prev.net_sales     || 0) + (row.net_sales     || 0),
        gross_sales:   (prev.gross_sales   || 0) + (row.gross_sales   || 0),
        toast_guid:    prev.toast_guid ?? row.toast_guid,
      })
    }
  }
  const finalRows = [...dedup.values()].sort((a, b) =>
    (b.quantity_sold || 0) - (a.quantity_sold || 0)
  )

  return { rows: finalRows, columns: { ...cols, grossIdx }, headers, errors }
}

// ============================================================
// Schedule CSV import — Google Sheets / Excel / generic export
// ============================================================
// Auto-detects: staff name, date, start time, end time, role.
// Times can be 24-hour ("17:00") or 12-hour with am/pm ("5:00 PM").
// Dates accept the same formats as parseSalesCsv (ISO, M/D/Y, etc).

function detectScheduleColumns(headers) {
  const findBest = (target, anti = []) => {
    let bestIdx = -1
    let bestScore = 0
    headers.forEach((h, idx) => {
      const s = scoreHeader(h, target, anti)
      if (s > bestScore) { bestScore = s; bestIdx = idx }
    })
    return bestIdx
  }

  return {
    nameIdx:  findBest(['staff', 'employee', 'name', 'person'], ['role', 'position']),
    dateIdx:  findBest(['date', 'day', 'shift'],     ['start', 'end', 'time']),
    startIdx: findBest(['start'], ['end']),
    endIdx:   findBest(['end'],   ['start']),
    roleIdx:  findBest(['role', 'position', 'job'], []),
  }
}

// Parse a time cell into "HH:MM" 24-hour string, or null on failure.
// Accepts: "17:00", "5:00 PM", "5pm", "5:00pm", "5p", "5 PM"
function parseTimeOfDay(raw) {
  if (raw == null) return null
  const s = String(raw).trim().replace(/^"|"$/g, '').toLowerCase()
  if (!s) return null

  // 24-hour format: "17:00" or "17:00:00"
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m) {
    const h = Number(m[1]), mm = Number(m[2])
    if (h >= 0 && h < 24 && mm >= 0 && mm < 60) {
      return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
    }
  }
  // 12-hour with am/pm: "5:00 PM", "5pm", "5:00pm", "5 PM"
  m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)\.?$/)
  if (m) {
    let h = Number(m[1])
    const mm = m[2] ? Number(m[2]) : 0
    const half = m[3].startsWith('p') ? 'pm' : 'am'
    if (h === 12) h = 0
    if (half === 'pm') h += 12
    if (h >= 0 && h < 24 && mm >= 0 && mm < 60) {
      return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
    }
  }
  return null
}

export function parseScheduleCsv(text) {
  const errors = []
  const grid = parseCsv(text)
  if (grid.length < 2) {
    return {
      rows: [],
      columns: null,
      headers: grid[0] ?? [],
      errors: ['CSV looks empty — only a header row was found.'],
    }
  }

  const headers = grid[0].map((h) => h.trim())
  const cols = detectScheduleColumns(headers)

  const missing = []
  if (cols.nameIdx === -1)  missing.push('staff name')
  if (cols.dateIdx === -1)  missing.push('date')
  if (cols.startIdx === -1) missing.push('start time')
  if (cols.endIdx === -1)   missing.push('end time')
  if (missing.length > 0) {
    return {
      rows: [],
      columns: cols,
      headers,
      errors: [
        `Missing required column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}. ` +
        `Expected headers like "Staff", "Date", "Start", "End".`,
      ],
    }
  }

  const rows = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    if (cells.length === 0) continue
    const name = String(cells[cols.nameIdx] ?? '').trim().replace(/^"|"$/g, '')
    if (!name) {
      errors.push(`Row ${r + 1}: skipped — staff name was empty`)
      continue
    }
    const sale_date = parseDate(cells[cols.dateIdx])
    if (!sale_date) {
      errors.push(`Row ${r + 1}: skipped — couldn't parse date "${cells[cols.dateIdx] ?? ''}"`)
      continue
    }
    const start_time = parseTimeOfDay(cells[cols.startIdx])
    const end_time   = parseTimeOfDay(cells[cols.endIdx])
    if (!start_time || !end_time) {
      errors.push(`Row ${r + 1}: skipped — couldn't parse times "${cells[cols.startIdx]}" / "${cells[cols.endIdx]}"`)
      continue
    }
    const role = cols.roleIdx >= 0
      ? String(cells[cols.roleIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null

    rows.push({
      name,
      shift_date: sale_date,
      start_time,
      end_time,
      role,
    })
  }

  return { rows, columns: cols, headers, errors }
}

// ============================================================
// Staff CSV import — bulk-add employees from a roster sheet
// ============================================================
// Auto-detects: name (required), role, hourly wage, email, phone, notes.
// Exists separately from parseScheduleCsv because the column space is
// different (schedule has dates + times; staff has contact info + wage).

function detectStaffColumns(headers) {
  const findBest = (target, anti = []) => {
    let bestIdx = -1
    let bestScore = 0
    headers.forEach((h, idx) => {
      const s = scoreHeader(h, target, anti)
      if (s > bestScore) { bestScore = s; bestIdx = idx }
    })
    return bestIdx
  }

  return {
    nameIdx:  findBest(['name', 'employee', 'staff', 'person'], ['role', 'position', 'job']),
    roleIdx:  findBest(['role', 'position', 'job', 'title'], ['email', 'phone', 'wage']),
    wageIdx:  findBest(['wage', 'rate', 'pay', 'hourly', 'salary'], []),
    emailIdx: findBest(['email', 'e-mail', 'mail'], []),
    phoneIdx: findBest(['phone', 'mobile', 'cell', 'tel', 'number'], []),
    notesIdx: findBest(['notes', 'comments', 'comment', 'remarks'], []),
  }
}

export function parseStaffCsv(text) {
  const errors = []
  const grid = parseCsv(text)
  if (grid.length < 2) {
    return {
      rows: [],
      columns: null,
      headers: grid[0] ?? [],
      errors: ['CSV looks empty — only a header row was found.'],
    }
  }

  const headers = grid[0].map((h) => h.trim())
  const cols = detectStaffColumns(headers)

  if (cols.nameIdx === -1) {
    return {
      rows: [],
      columns: cols,
      headers,
      errors: [
        'Could not find a name column. Expected a header containing "name", "employee", or "staff".',
      ],
    }
  }

  const rows = []
  for (let r = 1; r < grid.length; r++) {
    const cells = grid[r]
    if (cells.length === 0) continue
    const rawName = String(cells[cols.nameIdx] ?? '').trim().replace(/^"|"$/g, '')
    if (!rawName) {
      errors.push(`Row ${r + 1}: skipped — name column was empty`)
      continue
    }
    // Collapse internal whitespace so "Sarah  Lopez" (typo) and
    // "Sarah Lopez" match identically against the existing-staff map.
    const name = rawName.replace(/\s+/g, ' ').trim()
    // Require first + last name. Without a last name, any future
    // staff that share a first name would collide on import — better
    // to reject up front and tell the manager to fix the file than
    // silently merge two different people.
    if (name.split(' ').length < 2) {
      errors.push(`Row ${r + 1}: skipped — "${name}" needs a last name (first + last required for safe matching)`)
      continue
    }
    const role = cols.roleIdx >= 0
      ? String(cells[cols.roleIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null
    const hourly_wage = cols.wageIdx >= 0 ? parseMoney(cells[cols.wageIdx]) : null
    const email = cols.emailIdx >= 0
      ? String(cells[cols.emailIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null
    const phone = cols.phoneIdx >= 0
      ? String(cells[cols.phoneIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null
    const notes = cols.notesIdx >= 0
      ? String(cells[cols.notesIdx] ?? '').trim().replace(/^"|"$/g, '') || null
      : null

    rows.push({ name, role, hourly_wage, email, phone, notes })
  }

  // Deduplicate by normalized name (lowercase, single-spaced). Last
  // occurrence wins for any given name so a re-tweaked row at the
  // bottom of the sheet overrides an earlier one.
  const dedup = new Map()
  for (const row of rows) {
    dedup.set(normalizeStaffName(row.name), row)
  }
  const finalRows = [...dedup.values()].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return { rows: finalRows, columns: cols, headers, errors }
}

// Shared normalization used by the parser, the hook (importStaffFromCsv),
// and the modal preview. Lowercase + collapse whitespace so the same
// name written three different ways still matches the same record.
export function normalizeStaffName(name) {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

